import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormularioCampo } from '@/types/formulario';
import { useFormularioSubmit } from '@/hooks/useFormularioSubmit';
import { useFormularioAreas } from '@/hooks/useFormularioAreas';
import { usePlanoAcaoTrigger } from '@/hooks/usePlanoAcaoTrigger';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { FileUpload } from '@/components/ui/file-upload';
import { SignaturePad } from '@/components/ui/signature-pad';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { MapPin, Save, Send, AlertTriangle, ClipboardList, ChevronLeft, ChevronRight, CheckCircle, PenTool, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { PlanoAcaoModal } from './PlanoAcaoModal';
interface FormularioRendererProps {
  formularioId: string;
  formularioTitulo: string;
  formularioDescricao?: string;
  campos: FormularioCampo[];
  respostaId?: string;
  onCancel?: () => void;
  onSubmitSuccess?: (respostaId: string) => void;
  isPublic?: boolean;
  allowAnonymous?: boolean;
  clientId?: string;
  solicitacaoNumero?: string;
  solicitacaoTitulo?: string;
}
export function FormularioRenderer({
  formularioId,
  formularioTitulo,
  formularioDescricao,
  campos,
  respostaId,
  onCancel,
  onSubmitSuccess,
  isPublic = false,
  allowAnonymous = false,
  clientId,
  solicitacaoNumero,
  solicitacaoTitulo
}: FormularioRendererProps) {
  const form = useForm();
  
  // Observar TODOS os valores do formulário para disparar re-renderização
  const watchAllFields = form.watch();
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [camposPorPagina, setCamposPorPagina] = useState(10);
  
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [localRespostaId, setLocalRespostaId] = useState(respostaId);
  const {
    areas
  } = useFormularioAreas(formularioId);
  const {
    verificarDisparo
  } = usePlanoAcaoTrigger();
  const [planoAcaoModalState, setPlanoAcaoModalState] = useState<{
    isOpen: boolean;
    campoId: string;
    campoRespostaId?: string;
    tituloSugestao?: string;
    justificativaSugestao?: string;
  }>({
    isOpen: false,
    campoId: ''
  });
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  
  // Estados para planos de ação pós-envio
  const [showPlanosAcaoPendentes, setShowPlanosAcaoPendentes] = useState(false);
  const [camposPendentesPlanoAcao, setCamposPendentesPlanoAcao] = useState<{
    campoId: string;
    label: string;
    tituloSugestao?: string;
    justificativaSugestao?: string;
    obrigatorio?: boolean;
  }[]>([]);
  const [planosJaCriados, setPlanosJaCriados] = useState<string[]>([]);

  // Verificar se há mudanças no formulário
  const hasChanges = form.formState.isDirty && !isFormSubmitted;

  // Função para verificar se um campo está preenchido baseado no tipo
  const isFieldFilled = (campo: FormularioCampo, value: any): boolean => {
    switch (campo.tipo_campo) {
      case 'checkbox':
        // Checkbox simples: valor booleano true
        // Checkbox com opcoes (renderizado como RadioGroup): valor string nao vazia
        if (typeof value === 'boolean') return value === true;
        if (typeof value === 'string') return value.trim() !== '';
        return false;
      
      case 'arquivo':
      case 'foto':
        // Arquivos precisam ter pelo menos um item no array
        return Array.isArray(value) && value.length > 0;
      
      case 'radio':
      case 'select':
        // Radio e select precisam ter uma opção selecionada (string não vazia)
        return typeof value === 'string' && value.trim() !== '';
      
      case 'escala':
        // Escala aceita 0 como valor válido
        return value !== undefined && value !== null && value !== '';
      
      case 'numero':
        // Número aceita 0 como valor válido
        return value !== undefined && value !== null && value !== '';
      
      case 'texto':
      case 'textarea':
        // Texto precisa ter conteúdo não vazio
        return typeof value === 'string' && value.trim() !== '';
      
      case 'data':
      case 'hora':
        // Data e hora precisam ter valor definido
        return typeof value === 'string' && value !== '';
      
      case 'localizacao':
        // Localização precisa ter coordenadas
        return value && typeof value === 'object' && 
               'latitude' in value && 'longitude' in value;
      
      case 'assinatura':
        // Assinatura precisa ter valor (geralmente base64)
        return typeof value === 'string' && value !== '';
      
      default:
        // Fallback: valor existe e não é vazio
        return value !== undefined && value !== null && value !== '';
    }
  };

  // Bloquear fechamento da aba/navegador (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Ordenar campos por ordem para numeração global
  const camposOrdenados = React.useMemo(() => 
    [...campos].sort((a, b) => a.ordem - b.ordem), 
    [campos]
  );

  // Calcular paginação
  const usarPaginacao = camposOrdenados.length > 10;
  const totalPaginas = camposPorPagina === -1 ? 1 : Math.ceil(camposOrdenados.length / camposPorPagina);
  
  // Campos da página atual
  const camposPaginados = React.useMemo(() => {
    if (!usarPaginacao || camposPorPagina === -1) return camposOrdenados;
    const inicio = (currentPage - 1) * camposPorPagina;
    const fim = inicio + camposPorPagina;
    return camposOrdenados.slice(inicio, fim);
  }, [camposOrdenados, currentPage, camposPorPagina, usarPaginacao]);

  // Reset página ao mudar quantidade
  useEffect(() => {
    setCurrentPage(1);
  }, [camposPorPagina]);

  // Função para obter índice global de um campo
  const getGlobalIndex = React.useCallback((campoId: string) => {
    return camposOrdenados.findIndex(c => c.id === campoId) + 1;
  }, [camposOrdenados]);

  // Agrupar campos paginados por área
  const camposPorArea = React.useMemo(() => {
    const grupos: Record<string, FormularioCampo[]> = {
      'sem-area': []
    };
    areas.forEach(area => {
      grupos[area.id] = [];
    });
    camposPaginados.forEach(campo => {
      if (campo.area_id && grupos[campo.area_id]) {
        grupos[campo.area_id].push(campo);
      } else {
        grupos['sem-area'].push(campo);
      }
    });
    return grupos;
  }, [camposPaginados, areas]);
  const { 
    isSaving,
    uploadFile, 
    captureLocation, 
    submitFormulario 
  } = useFormularioSubmit({
    respostaId: localRespostaId || '',
    form,
    onSubmitSuccess: onSubmitSuccess || (() => onCancel?.()),
    isPublic,
    formularioId,
    clientId
  });

  // Calcular progresso baseado em campos obrigatórios preenchidos
  const progresso = React.useMemo(() => {
    const camposObrigatorios = campos.filter(c => c.obrigatorio);
    const totalCampos = camposObrigatorios.length;
    
    if (totalCampos === 0) return 0;
    
    const camposPreenchidos = camposObrigatorios.filter(campo => {
      const value = watchAllFields[campo.id];
      return isFieldFilled(campo, value);
    }).length;
    
    return Math.round((camposPreenchidos / totalCampos) * 100);
  }, [campos, watchAllFields]);
  const handleCaptureLocation = async () => {
    try {
      const loc = await captureLocation();
      setLocation(loc);
      toast.success('Localização capturada com sucesso!');
    } catch (error: any) {
      toast.error('Erro ao capturar localização: ' + error.message);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar todos os campos
    const isValid = await form.trigger();
    
    if (!isValid) {
      // Coletar campos obrigatórios não preenchidos
      const camposComErro = campos.filter(campo => {
        const error = form.formState.errors[campo.id];
        return campo.obrigatorio && error;
      });

      if (camposComErro.length > 0) {
        // Scroll até o primeiro campo com erro
        const firstErrorField = document.querySelector(`[name="${camposComErro[0].id}"]`);
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Exibir toast com campos faltantes
        toast.error(
          `Por favor, preencha os campos obrigatórios: ${camposComErro.map(c => c.label).join(', ')}`,
          { duration: 5000 }
        );
      }
      
      return;
    }

    // Se válido, submeter
    try {
      setIsFormSubmitted(true); // Marcar como submetido para permitir navegação
      const novoRespostaId = await submitFormulario();
      
      // Verificar quais campos precisam de plano de ação
      const pendentes = campos.filter(campo => {
        const valor = form.getValues(campo.id);
        const config = campo.configuracoes?.plano_acao;
        return config?.habilitado && verificarDisparo(campo, valor);
      }).map(campo => ({
        campoId: campo.id,
        label: campo.label,
        tituloSugestao: campo.configuracoes?.plano_acao?.campos_pre_preenchidos?.titulo_padrao,
        justificativaSugestao: campo.configuracoes?.plano_acao?.campos_pre_preenchidos?.justificativa_padrao,
        obrigatorio: campo.configuracoes?.plano_acao?.obrigatorio,
      }));
      
      if (pendentes.length > 0) {
        setLocalRespostaId(novoRespostaId);
        setCamposPendentesPlanoAcao(pendentes);
        setShowPlanosAcaoPendentes(true);
        toast.info(`${pendentes.length} campo(s) requerem plano de ação`);
      } else {
        // Sem planos pendentes - chamar callback de sucesso
        onSubmitSuccess?.(novoRespostaId);
      }
    } catch (error: any) {
      setIsFormSubmitted(false); // Reverter em caso de erro
      console.error('Erro ao enviar formulário:', error);
    }
  };

  const handleCancelClick = () => {
    if (hasChanges) {
      setShowExitConfirm(true);
    } else {
      onCancel?.();
    }
  };

  // Callback quando um plano de ação é criado com sucesso
  const handlePlanoAcaoCriado = (campoId: string) => {
    setPlanosJaCriados(prev => [...prev, campoId]);
  };

  // Verificar se há planos obrigatórios pendentes
  const temPlanosPendentesObrigatorios = camposPendentesPlanoAcao
    .filter(p => p.obrigatorio)
    .some(p => !planosJaCriados.includes(p.campoId));

  // Finalizar após criar planos de ação
  const handleFinalizarComPlanos = () => {
    if (temPlanosPendentesObrigatorios) {
      toast.error('Existem planos de ação obrigatórios pendentes');
      return;
    }
    toast.success('Formulário finalizado com sucesso!');
    onSubmitSuccess?.(localRespostaId!);
    onCancel?.();
  };

  // Abrir modal de plano de ação para campo pendente
  const openPlanoModalPendente = (pendente: typeof camposPendentesPlanoAcao[0]) => {
    setPlanoAcaoModalState({
      isOpen: true,
      campoId: pendente.campoId,
      tituloSugestao: pendente.tituloSugestao,
      justificativaSugestao: pendente.justificativaSugestao,
    });
  };
  const renderField = (campo: FormularioCampo) => {
    const globalIndex = getGlobalIndex(campo.id);
    const numberedLabel = `${globalIndex} - ${campo.label}`;
    const planoAcaoConfig = campo.configuracoes?.plano_acao;
    const valorAtual = form.watch(campo.id);
    const deveExibirBotao = planoAcaoConfig?.habilitado && verificarDisparo(campo, valorAtual);
    const renderFieldContent = () => {
      switch (campo.tipo_campo) {
        case 'texto':
          return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
            required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
          }} render={({
            field
          }) => <FormItem>
                <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormControl>
                  <Input {...field} placeholder={campo.configuracoes?.placeholder} />
                </FormControl>
                <FormMessage />
              </FormItem>} />;
        case 'numero':
          return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
            required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
          }} render={({
            field
          }) => <FormItem>
                <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormControl>
                  <Input {...field} type="number" min={campo.configuracoes?.min} max={campo.configuracoes?.max} />
                </FormControl>
                <FormMessage />
              </FormItem>} />;
        case 'textarea':
          return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
            required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
          }} render={({
            field
          }) => <FormItem>
                <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormControl>
                  <Textarea {...field} placeholder={campo.configuracoes?.placeholder} />
                </FormControl>
                <FormMessage />
              </FormItem>} />;
        case 'select':
          return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
            required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
          }} render={({
            field
          }) => <FormItem>
                <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma opção" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {campo.configuracoes?.opcoes?.map((opcao: string, idx: number) => <SelectItem key={idx} value={opcao}>
                        {opcao}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />;
        case 'radio':
          const layoutRadio = campo.configuracoes?.layout_opcoes || 'vertical';
          const isHorizontalRadio = layoutRadio === 'horizontal';
          
          return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
            required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
          }} render={({
            field
          }) => <FormItem>
                <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormControl>
                  <RadioGroup 
                    onValueChange={field.onChange} 
                    value={field.value}
                    className={cn(isHorizontalRadio && "flex flex-wrap gap-4")}
                  >
                    {campo.configuracoes?.opcoes?.map((opcao: string, idx: number) => <div 
                        key={idx} 
                        className={cn(
                          "flex items-center space-x-2",
                          isHorizontalRadio && "flex-shrink-0"
                        )}
                      >
                        <RadioGroupItem value={opcao} id={`${campo.id}-${idx}`} />
                        <Label htmlFor={`${campo.id}-${idx}`}>{opcao}</Label>
                      </div>)}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />;
        case 'checkbox':
          // Se tem opções configuradas (ex: Sim/Não), renderizar como radio
          if (campo.configuracoes?.opcoes && campo.configuracoes.opcoes.length > 0) {
            const layoutOpcoes = campo.configuracoes?.layout_opcoes || 'horizontal';
            const isHorizontal = layoutOpcoes === 'horizontal';
            
            return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
              required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
            }} render={({
              field
            }) => <FormItem>
                  <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                  {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                  <FormControl>
                    <RadioGroup 
                      onValueChange={field.onChange} 
                      value={field.value}
                      className={cn(isHorizontal && "flex flex-wrap gap-4")}
                    >
                      {campo.configuracoes.opcoes.map((opcao: string, idx: number) => (
                        <div 
                          key={idx} 
                          className={cn(
                            "flex items-center space-x-2",
                            isHorizontal && "flex-shrink-0"
                          )}
                        >
                          <RadioGroupItem value={opcao} id={`${campo.id}-${idx}`} />
                          <Label htmlFor={`${campo.id}-${idx}`}>{opcao}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />;
          }
          // Checkbox simples (sem opções)
          return <FormField key={campo.id} control={form.control} name={campo.id} render={({
            field
          }) => <FormItem>
                <div className="flex items-start space-x-2">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                    {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                  </div>
                </div>
                <FormMessage />
              </FormItem>} />;
        case 'data':
          return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
            required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
          }} render={({
            field
          }) => <FormItem>
                <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormControl>
                  <Input {...field} type="date" />
                </FormControl>
                <FormMessage />
              </FormItem>} />;
        case 'hora':
          return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
            required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
          }} render={({
            field
          }) => <FormItem>
                <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormControl>
                  <Input {...field} type="time" />
                </FormControl>
                <FormMessage />
              </FormItem>} />;
        case 'arquivo':
          return <div key={campo.id} className="space-y-2">
            <Label>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</Label>
            {campo.descricao && <p className="text-sm text-muted-foreground">{campo.descricao}</p>}
            <FileUpload onFilesChange={files => form.setValue(campo.id, files)} maxFiles={campo.configuracoes?.maxFiles || 5} acceptedFileTypes={campo.configuracoes?.acceptedTypes} maxFileSize={campo.configuracoes?.maxFileSize || 10} />
          </div>;
        case 'localizacao':
          return <div key={campo.id} className="space-y-2">
            <Label>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</Label>
            {campo.descricao && <p className="text-sm text-muted-foreground">{campo.descricao}</p>}
            <Button type="button" variant="outline" onClick={handleCaptureLocation}>
              <MapPin className="mr-2 h-4 w-4" />
              Capturar Localização
            </Button>
            {location && <p className="text-sm text-muted-foreground">
                Lat: {location.latitude.toFixed(6)}, Lng: {location.longitude.toFixed(6)}
              </p>}
          </div>;
        case 'escala':
          const tipoEscala = campo.configuracoes?.tipo_escala || 'numerica';
          const valoresTexto = campo.configuracoes?.valores_texto || [];
          const minimo = campo.configuracoes?.minimo || 1;
          const maximo = campo.configuracoes?.maximo || 5;
          const rotulos = campo.configuracoes?.rotulos || {};
          const totalValores = tipoEscala === 'texto' ? valoresTexto.length : maximo - minimo + 1;
          const usarSlider = totalValores > 5;
          const estiloVisualizacao = campo.configuracoes?.estilo_visualizacao || 'texto';
          const emojisConfig = campo.configuracoes?.emojis || {};
          const tipoGradiente = campo.configuracoes?.tipo_gradiente || 'automatico_vermelho_verde';
          const escalaInvertida = campo.configuracoes?.escala_invertida || false;
          
          // Gerar cores automaticamente
          const gerarCoresAutomaticas = (direcao: 'vermelho_verde' | 'verde_vermelho'): Record<number | string, string> => {
            const cores: Record<number | string, string> = {};
            for (let i = 0; i < totalValores; i++) {
              const chave = tipoEscala === 'numerica' ? minimo + i : i;
              const percent = i / Math.max(totalValores - 1, 1);
              let r, g, b;
              if (direcao === 'vermelho_verde') {
                if (percent < 0.5) {
                  r = 220; g = Math.floor(59 + (187 - 59) * (percent * 2)); b = 38;
                } else {
                  r = Math.floor(220 - (220 - 22) * ((percent - 0.5) * 2));
                  g = Math.floor(187 + (163 - 187) * ((percent - 0.5) * 2));
                  b = Math.floor(38 + (74 - 38) * ((percent - 0.5) * 2));
                }
              } else {
                const invertPercent = 1 - percent;
                if (invertPercent < 0.5) {
                  r = 220; g = Math.floor(59 + (187 - 59) * (invertPercent * 2)); b = 38;
                } else {
                  r = Math.floor(220 - (220 - 22) * ((invertPercent - 0.5) * 2));
                  g = Math.floor(187 + (163 - 187) * ((invertPercent - 0.5) * 2));
                  b = Math.floor(38 + (74 - 38) * ((invertPercent - 0.5) * 2));
                }
              }
              cores[chave] = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
            return cores;
          };
          
          let coresCalculadas: Record<number | string, string> = {};
          if (estiloVisualizacao === 'cores' || estiloVisualizacao === 'texto_cor') {
            if (tipoGradiente === 'manual') {
              coresCalculadas = campo.configuracoes?.cores || {};
            } else {
              coresCalculadas = gerarCoresAutomaticas(
                tipoGradiente === 'automatico_vermelho_verde' ? 'vermelho_verde' : 'verde_vermelho'
              );
            }
            
            // Inverter cores se necessário
            if (escalaInvertida) {
              const coresInvertidas: Record<number | string, string> = {};
              for (let i = 0; i < totalValores; i++) {
                const chaveOriginal = tipoEscala === 'numerica' ? minimo + i : i;
                const chaveInvertida = tipoEscala === 'numerica' ? maximo - i : totalValores - 1 - i;
                coresInvertidas[chaveOriginal] = coresCalculadas[chaveInvertida];
              }
              coresCalculadas = coresInvertidas;
            }
          }
          
          // Inverter emojis se necessário
          let emojisAplicados = { ...emojisConfig };
          if (escalaInvertida && (estiloVisualizacao === 'emojis' || estiloVisualizacao === 'texto_emoji')) {
            const emojisInvertidos: Record<string, string> = {};
            for (let i = 0; i < totalValores; i++) {
              const chaveOriginal = tipoEscala === 'numerica' ? minimo + i : i;
              const chaveInvertida = tipoEscala === 'numerica' ? maximo - i : totalValores - 1 - i;
              emojisInvertidos[chaveOriginal] = emojisConfig[chaveInvertida] || '';
            }
            emojisAplicados = emojisInvertidos;
          }
          
          // Inverter valores de texto se necessário
          let valoresTextoAplicados = valoresTexto;
          if (escalaInvertida && tipoEscala === 'texto') {
            valoresTextoAplicados = [...valoresTexto].reverse();
          }
          
          return <FormField key={campo.id} control={form.control} name={campo.id} rules={{
            required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
          }} render={({
            field
          }) => {
            const valorAtual = field.value !== undefined ? field.value : (tipoEscala === 'numerica' ? minimo : 0);
            const valorInicial = tipoEscala === 'numerica' ? minimo : 0;
            const valorMaximo = tipoEscala === 'numerica' ? maximo : totalValores - 1;
            
            return <FormItem>
                  <FormLabel>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</FormLabel>
                  {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                  <FormControl>
                    <div className="space-y-4">
                      {usarSlider ?
                  // Slider para escalas grandes (mais de 5 valores)
                  <>
                          <div className="pt-2">
                            <Slider 
                              min={valorInicial} 
                              max={valorMaximo} 
                              step={1} 
                              value={[valorAtual]} 
                              onValueChange={values => field.onChange(values[0])} 
                              className="w-full" 
                            />
                          </div>
                          
                          {/* Rótulos das extremidades */}
                          <div className="flex justify-between text-sm text-muted-foreground px-1">
                            <div className="text-left">
                              <div className="font-medium">
                                {tipoEscala === 'texto' ? valoresTextoAplicados[0] : minimo}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">
                                {tipoEscala === 'texto' ? valoresTextoAplicados[totalValores - 1] : maximo}
                              </div>
                            </div>
                          </div>
                        </> :
                  // Botões para escalas pequenas (5 ou menos valores)
                  <>
                          <div className={cn(
                            "flex gap-3 sm:gap-2 px-1 sm:px-0",
                            // Layout adaptativo baseado em quantidade de valores
                            totalValores > 3 && totalValores <= 5 
                              ? "grid grid-cols-2 sm:flex sm:flex-row gap-3" 
                              : totalValores > 5 
                                ? "flex-col sm:flex-row" 
                                : "flex-row justify-between"
                          )}>
                            {Array.from({
                        length: totalValores
                      }, (_, i) => {
                        const chave = tipoEscala === 'numerica' ? minimo + i : i;
                        const isSelected = valorAtual === chave;
                        const emoji = emojisAplicados[chave];
                        const cor = coresCalculadas[chave];
                        const rotulo = tipoEscala === 'texto' 
                          ? valoresTextoAplicados[i] 
                          : (rotulos[chave] || chave);
                        
                        const buttonStyle: React.CSSProperties = {};
                        if (estiloVisualizacao === 'cores' || estiloVisualizacao === 'texto_cor') {
                          buttonStyle.backgroundColor = isSelected ? cor : `${cor}40`;
                          buttonStyle.borderColor = cor;
                          buttonStyle.color = isSelected ? '#ffffff' : '#1f2937';
                        }
                        
                        return <Button 
                          key={chave} 
                          type="button" 
                          variant={isSelected && (estiloVisualizacao !== 'cores' && estiloVisualizacao !== 'texto_cor') ? "default" : "outline"} 
                          size="lg" 
                          onClick={() => field.onChange(chave)} 
                          className={cn(
                            "h-14 sm:h-16 min-w-[60px] sm:min-w-0 px-3 sm:px-4 flex flex-col gap-1 transition-all touch-manipulation",
                            totalValores <= 3 ? "sm:flex-1" : "flex-1",
                            isSelected && "ring-2 ring-primary shadow-lg sm:scale-105 active:scale-95",
                            !isSelected && "sm:hover:scale-105 active:scale-95"
                          )}
                          style={buttonStyle}
                        >
                          {estiloVisualizacao === 'emojis' && (
                            <span className="text-base sm:text-lg">{emoji || '❓'}</span>
                          )}
                          
                          {estiloVisualizacao === 'texto' && (
                            <span className="text-sm sm:text-base font-medium text-center">{rotulo}</span>
                          )}
                          
                          {estiloVisualizacao === 'texto_emoji' && (
                            <>
                              <span className="text-xs sm:text-sm">{emoji || '❓'}</span>
                              <span className="text-[10px] sm:text-xs font-medium text-center leading-tight line-clamp-2">{rotulo}</span>
                            </>
                          )}
                          
                          {(estiloVisualizacao === 'cores' || estiloVisualizacao === 'texto_cor') && (
                            <span className="text-sm sm:text-base font-medium text-center line-clamp-2">{rotulo}</span>
                          )}
                        </Button>;
                      })}
                          </div>
                        </>}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>;
          }} />;
        case 'foto':
          return <div key={campo.id} className="space-y-2">
            <Label>{numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}</Label>
            {campo.descricao && <p className="text-sm text-muted-foreground">{campo.descricao}</p>}
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 p-4 border-2 border-dashed rounded-md bg-muted/50 w-full">
                <Camera className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Capturar ou selecionar foto</span>
              </div>
              <FileUpload 
                onFilesChange={files => form.setValue(campo.id, files)} 
                maxFiles={1}
                acceptedFileTypes="image/*"
                maxFileSize={campo.configuracoes?.maxFileSize || 10} 
              />
            </div>
          </div>;
        case 'assinatura':
          return <FormField 
            key={campo.id} 
            control={form.control} 
            name={campo.id}
            rules={{
              required: campo.obrigatorio ? `${campo.label} é obrigatório` : undefined
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {numberedLabel} {campo.obrigatorio && <span className="text-destructive">*</span>}
                </FormLabel>
                {campo.descricao && (
                  <FormDescription>{campo.descricao}</FormDescription>
                )}
                <FormControl>
                  <SignaturePad
                    width={400}
                    height={200}
                    initialValue={field.value}
                    onSignatureChange={(dataUrl) => {
                      form.setValue(campo.id, dataUrl || undefined);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />;
        default:
          return null;
      }
    };
    return <div key={campo.id}>
        {renderFieldContent()}
        
        {/* Botão de Plano de Ação Condicional */}
        {deveExibirBotao && <div className="flex items-center gap-2 mt-3 p-3 bg-orange-50 border-l-4 border-orange-500 rounded">
            <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <p className="text-sm text-orange-800">
              {planoAcaoConfig?.config_botao?.descricao || 'Esta resposta requer um plano de ação'}
            </p>
          </div>}
      </div>;
  };
  const containerClass = isPublic ? "flex items-center justify-center py-2 px-4" : "fixed inset-0 z-50 bg-background/80 backdrop-blur-sm";
  const innerClass = isPublic ? "w-full max-w-3xl" : "fixed inset-y-0 right-0 w-full md:w-3/4 lg:w-2/3 bg-background shadow-lg overflow-y-auto";
  return <div className={containerClass}>
      <div className={innerClass}>
        <div className={isPublic ? "" : "container max-w-4xl py-8"}>
          <Card>
            <CardHeader>
              {/* Logo Header */}
              <div className="flex items-center justify-center mb-6">
                <img 
                  src="/logos/Logo_Aroan_original.png" 
                  alt="Aroan Logo" 
                  className="h-12 w-auto"
                />
              </div>
              
              {/* Contexto do ticket (pesquisa de satisfação) */}
              {solicitacaoNumero && (
                <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardList className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Avalie o atendimento recebido</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Ticket <span className="font-medium">{solicitacaoNumero}</span>
                    {solicitacaoTitulo && <> — {solicitacaoTitulo}</>}
                  </p>
                </div>
              )}

              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-2xl">{formularioTitulo}</CardTitle>
                  {formularioDescricao && <CardDescription className="mt-2">{formularioDescricao}</CardDescription>}
                </div>
                {!isPublic && onCancel && <Button variant="ghost" onClick={onCancel}>✕</Button>}
              </div>
              
              {/* Progress Bar */}
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progresso</span>
                  <span>{progresso}%</span>
                </div>
                <Progress value={progresso} />
              </div>
              
              {/* Controles de paginação - topo */}
              {usarPaginacao && (
                <div className="flex flex-wrap items-center justify-between gap-4 p-3 mt-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Campos por página:</span>
                    <Select value={String(camposPorPagina)} onValueChange={(v) => setCamposPorPagina(Number(v))}>
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="15">15</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="-1">Todos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {camposPorPagina !== -1 && (
                    <>
                      <span className="text-sm text-muted-foreground">
                        Página {currentPage} de {totalPaginas} ({camposOrdenados.length} campos)
                      </span>
                      
                      <div className="flex gap-2">
                        <Button 
                          type="button"
                          size="sm" 
                          variant="outline" 
                          onClick={() => setCurrentPage(p => p - 1)} 
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" /> Anterior
                        </Button>
                        <Button 
                          type="button"
                          size="sm" 
                          variant="outline" 
                          onClick={() => setCurrentPage(p => p + 1)} 
                          disabled={currentPage === totalPaginas}
                        >
                          Próxima <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardHeader>

            <CardContent>
              <Form {...form}>
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  {/* Áreas */}
                  {areas.length > 0 && <Accordion type="multiple" defaultValue={areas.filter(a => a.expandida_por_padrao).map(a => a.id)}>
                      {areas.map(area => {
                    const camposArea = camposPorArea[area.id] || [];
                    if (camposArea.length === 0) return null;
                    return <AccordionItem key={area.id} value={area.id}>
                            <AccordionTrigger className="text-lg font-semibold">{area.titulo}</AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-4 pt-2">
                                {camposArea.sort((a, b) => a.ordem - b.ordem).map(renderField)}
                              </div>
                            </AccordionContent>
                          </AccordionItem>;
                  })}
                    </Accordion>}
                  
                  {/* Campos sem área */}
                  {camposPorArea['sem-area'].sort((a, b) => a.ordem - b.ordem).map(renderField)}

                  {/* Controles de paginação - rodapé */}
                  {usarPaginacao && camposPorPagina !== -1 && (
                    <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t">
                      <span className="text-sm text-muted-foreground">
                        Mostrando {((currentPage - 1) * camposPorPagina) + 1} - {Math.min(currentPage * camposPorPagina, camposOrdenados.length)} de {camposOrdenados.length}
                      </span>
                      
                      <div className="flex gap-2">
                        <Button 
                          type="button"
                          size="sm" 
                          variant="outline" 
                          onClick={() => setCurrentPage(p => p - 1)} 
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" /> Anterior
                        </Button>
                        <Button 
                          type="button"
                          size="sm" 
                          variant="outline" 
                          onClick={() => setCurrentPage(p => p + 1)} 
                          disabled={currentPage === totalPaginas}
                        >
                          Próxima <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 pt-6 border-t">
                    {!isPublic && onCancel && <Button type="button" variant="outline" onClick={handleCancelClick} disabled={isSaving}>
                        Cancelar
                      </Button>}
                    {/* Mostrar botão enviar apenas na última página ou quando não há paginação */}
                    {(!usarPaginacao || camposPorPagina === -1 || currentPage === totalPaginas) && (
                      <Button type="submit" disabled={isSaving} className="flex-1">
                        <Send className="mr-2 h-4 w-4" />
                        {isSaving ? 'Enviando...' : 'Enviar Formulário'}
                      </Button>
                    )}
                    {/* Botão Próxima Página quando não está na última */}
                    {usarPaginacao && camposPorPagina !== -1 && currentPage < totalPaginas && (
                      <Button 
                        type="button" 
                        onClick={() => setCurrentPage(p => p + 1)} 
                        className="flex-1"
                      >
                        Próxima Página <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
          
          {/* Seção de Planos de Ação Pendentes (após envio) */}
          {showPlanosAcaoPendentes && localRespostaId && (
            <Card className="mt-4 border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="h-5 w-5" />
                  Formulário Enviado - Planos de Ação Pendentes
                </CardTitle>
                <CardDescription className="text-orange-700">
                  Algumas respostas requerem a criação de plano de ação. 
                  Por favor, crie os planos abaixo antes de finalizar.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {camposPendentesPlanoAcao.map((pendente) => (
                  <div key={pendente.campoId} className="flex items-center justify-between p-3 bg-white rounded border">
                    <div className="flex-1">
                      <p className="font-medium">{pendente.label}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {planosJaCriados.includes(pendente.campoId) ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Plano criado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-orange-300 text-orange-700">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Pendente {pendente.obrigatorio && '(obrigatório)'}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button 
                      onClick={() => openPlanoModalPendente(pendente)}
                      variant={planosJaCriados.includes(pendente.campoId) ? "outline" : "default"}
                    >
                      <ClipboardList className="h-4 w-4 mr-2" />
                      {planosJaCriados.includes(pendente.campoId) ? 'Editar Plano' : 'Criar Plano'}
                    </Button>
                  </div>
                ))}
                
                {/* Botão finalizar */}
                <Button 
                  className="w-full mt-4" 
                  onClick={handleFinalizarComPlanos}
                  disabled={temPlanosPendentesObrigatorios}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Finalizar
                </Button>
                
                {temPlanosPendentesObrigatorios && (
                  <p className="text-sm text-orange-700 text-center">
                    Crie os planos de ação obrigatórios para finalizar
                  </p>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Modal de Plano de Ação */}
          {localRespostaId && (
            <PlanoAcaoModal 
              isOpen={planoAcaoModalState.isOpen} 
              onClose={() => setPlanoAcaoModalState({
                ...planoAcaoModalState,
                isOpen: false
              })} 
              campoRespostaId={planoAcaoModalState.campoRespostaId} 
              respostaId={localRespostaId} 
              campoId={planoAcaoModalState.campoId} 
              tituloSugestao={planoAcaoModalState.tituloSugestao} 
              justificativaSugestao={planoAcaoModalState.justificativaSugestao}
              onSuccess={() => handlePlanoAcaoCriado(planoAcaoModalState.campoId)}
            />
          )}
        
          {/* Modal de Confirmação de Saída */}
          <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Tem certeza que deseja sair?</AlertDialogTitle>
                <AlertDialogDescription>
                  Você tem dados não salvos. Se sair agora, suas alterações serão perdidas.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Continuar preenchendo</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                  setIsFormSubmitted(true);
                  onCancel?.();
                }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Sair sem enviar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>;
}